import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DeleteModalComponent } from "src/app/shared/components/delete-modal/delete-modal.component";
import { allowDeleteEntity, allowUpdateEntity } from "src/app/shared/utils/helpers/permission.helper";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { LoadingService } from "../../../core/loader/loading.service";
import { DataTableConfig } from "../../../shared/components/data-table/data-table-service.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import {
  DataTable,
  DataTableColumn,
  GetDataEvent,
  UpdateDataEvent,
} from "../../../shared/components/data-table/data-table.type";
import { NotificationService } from "../../../shared/components/notification/notification.service";
import { BoolToStringPipe } from "../../../shared/pipes/bool-to-string/bool-to-string.pipe";
import { UserInfo } from "../../../shared/types/user-info";
import { PartnerSpecialTypes } from "../partners/partner.type";
import { getAssetAttributeSearchFilterOptions } from "./asset";
import { AssetService } from "./asset.service";
import { Asset, AssetAttribute } from "./asset.type";

@Injectable({
  providedIn: "root",
})
export class AssetAttributeTableService {
  currentUser: UserInfo;
  asset: Asset;

  constructor(
    private dialog: MatDialog,
    private loader: LoadingService,
    private authenticationService: AuthenticationService,
    private assetService: AssetService,
    private notificationService: NotificationService,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
  }

  getAssetAttributesTableConfig = (config?: DataTableConfig): DataTable<AssetAttribute> => {
    const { hidden, shown, actionColumn, entities } = config || {};

    this.asset = entities.asset;

    const isRelutech =
      this.currentUser.partner?.specialType === PartnerSpecialTypes.Relutech ||
      this.currentUser.company?.partner?.specialType === PartnerSpecialTypes.Relutech;

    const columns: DataTableColumn<AssetAttribute>[] = (
      [
        {
          prop: "name",
          name: "Attribute Name",
          render: (assetAttribute) => assetAttribute.attribute.name,
        },
        {
          prop: "type",
          name: "Attribute Type",
          render: (assetAttribute) => assetAttribute.attribute.type,
        },
        {
          prop: "quantity",
          name: "Quantity",
        },
        {
          prop: "buyPrice",
          name: "Buy Price",
          render: (assetAttribute) => assetAttribute.attribute.buyPrice,
          hidden: !isRelutech && !this.currentUser.isGlobal(),
        },
        {
          prop: "default",
          name: "Default",
          pipes: [{ clazz: BoolToStringPipe }],
          headerTooltip: {
            icon: "info",
            message:
              "Default Attributes are added as assumptions. To report your exact configurations, please contact portal@nasupport.com",
          },
        },
        {
          prop: "deleted",
          name: "Deleted",
          pipes: [{ clazz: BoolToStringPipe }],
          hidden: !this.currentUser.isGlobalAdmin(),
        },
      ] as DataTableColumn<AssetAttribute>[]
    )
      .filter((col) => (shown ? shown.includes(col.prop) : true) && (hidden ? !hidden.includes(col.prop) : true))
      .map((col) => ({
        ...col,
        inlineEdit: {
          ...col?.inlineEdit,
          /** Use the set condition and the new one passed here */
          disabled: (assetAttribute) =>
            col?.inlineEdit?.disabled?.(assetAttribute) || !allowUpdateEntity(assetAttribute, this.currentUser),
        },
      }));

    return {
      id: "assetAttribute",
      entityName: "Attribute",
      syncWithQueryParams: true,
      columns,
      defaultColumns: [],
      actionColumn: actionColumn?.hidden
        ? null
        : [
            {
              icon: "clear",
              text: "Delete",
              function: this.deleteAssetAttribute,
              hidden: (assetAttribute) =>
                assetAttribute.deleted || !allowDeleteEntity(assetAttribute, this.currentUser),
            },
            {
              icon: "restore",
              text: "Restore",
              function: this.restoreAssetAttribute,
              hidden: (assetAttribute) => !assetAttribute.deleted || !this.currentUser.isGlobalAdmin(),
            },
          ],
      searchBar: [
        {
          availableSearchFields: getAssetAttributeSearchFilterOptions(this.currentUser, { hidden, shown }),
        },
      ],
    };
  };

  getAssetAttributes = (event: GetDataEvent<AssetAttribute>, asset: Asset): void => {
    const { onError, onResult, queryParams } = event;
    this.assetService
      .getAssetAttributes(asset.id, {
        ...queryParams,
        where: {
          ...queryParams?.where,
        },
      })
      .subscribe(
        (data): void => {
          onResult(data);
        },
        (error): void => {
          onError(error);
        },
      );
  };

  update = (event: UpdateDataEvent<AssetAttribute>): void => {
    const { onError, onResult, row, updatedValues } = event;
    if (row.id && updatedValues) {
      this.loader.show();
      this.assetService.updateAttribute(row.attributeId, { ...updatedValues }).subscribe(
        (result): void => {
          onResult({ ...row, attribute: result });
          this.loader.hide();
        },
        (error): void => {
          onError(error);
          this.loader.hide();
        },
      );
    } else {
      onError();
    }
  };

  private deleteAssetAttribute = (assetAttribute: AssetAttribute, table: DataTableComponent<AssetAttribute>): void => {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      width: "300px",
      panelClass: "custom-dialog-container",
      disableClose: true,
      data: {
        name: "Asset Attribute",
        data: assetAttribute,
        overwrittenUrl: `${this.assetService.basePathAssetAttributes}/${assetAttribute.id}`,
        body: { assetId: this.asset?.id },
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        table.search({ isDeletingOrUnlinking: true });
      }
    });
  };

  private restoreAssetAttribute = (assetAttribute: AssetAttribute, table: DataTableComponent<AssetAttribute>): void => {
    this.loader.show();
    this.assetService
      .restoreAssetAttribute(assetAttribute.id)
      .subscribe(
        (): void => {
          table.search({ isDeletingOrUnlinking: true });
        },
        (error): void => {
          this.notificationService.error(error);
        },
      )
      .add(() => this.loader.hide());
  };
}
